import { useEffect, useState } from 'react';
import { useSuggestArticles } from '@/lib/adminApi';
import type { TicketSummary } from '@ventin/shared';

/**
 * KbSuggestPanel — debounced KB article suggestions (TKT-06).
 *
 * Debounces the ticket title (300ms) into a local `q` state, then calls
 * useSuggestArticles when q.length >= 3. Shows up to 5 article cards with
 * title + truncated body + "Open article" link (new tab).
 *
 * No new deps — debounce implemented locally per Phase 2 convention.
 */

interface Props {
  ticket: TicketSummary;
}

export function KbSuggestPanel({ ticket }: Props) {
  const [q, setQ] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setQ(ticket.title), 300);
    return () => clearTimeout(t);
  }, [ticket.title]);

  const enabled = q.length >= 3;
  const { data, isLoading } = useSuggestArticles(q, { enabled, limit: 5 });
  const articles = data?.articles ?? [];

  return (
    <div className="rounded-md border bg-background p-4">
      <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">
        KB Suggestions
      </h3>

      {!enabled && (
        <p className="text-sm text-muted-foreground">
          Suggestions will appear as you work this ticket
        </p>
      )}

      {enabled && isLoading && (
        <p className="text-sm text-muted-foreground">Loading suggestions…</p>
      )}

      {enabled && !isLoading && articles.length === 0 && (
        <p className="text-sm text-muted-foreground">No articles matched</p>
      )}

      {articles.length > 0 && (
        <ul className="space-y-3">
          {articles.map((article) => (
            <li key={article.id} className="border-b pb-3 last:border-b-0 last:pb-0">
              <p className="text-sm font-medium leading-snug">{article.title}</p>
              {article.body && (
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{article.body}</p>
              )}
              <a
                href={`/admin/knowledgebase/${article.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline mt-1 inline-block"
              >
                Open article
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
